import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { PageHeader } from "@/components/primitives";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Eye, EyeOff, Trash2, RefreshCcw, ArrowLeft, CheckSquare, Square, Send } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AdminCommunity() {
  const { user } = useAuth();
  const [blooms, setBlooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/community/blooms"); setBlooms(data); setSelected(new Set()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load submissions"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  if (user === null) return null;
  if (!user || !user.is_admin) return <Navigate to="/home" replace />;

  const pending = blooms.filter((b) => !b.released);
  const released = blooms.filter((b) => b.released);

  const setReleased = async (id, val) => {
    try {
      const { data } = await api.put(`/admin/community/blooms/${id}`, { released: val });
      setBlooms((prev) => prev.map((b) => (b.id === id ? data : b)));
      toast.success(val ? "Released" : "Hidden");
    } catch { toast.error("Could not update"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/community/blooms/${id}`);
      setBlooms((prev) => prev.filter((b) => b.id !== id));
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
  };

  const toggleSel = (id) => {
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const selectAllPending = () => setSelected(new Set(pending.map((b) => b.id)));
  const clearSel = () => setSelected(new Set());

  const bulkRelease = async () => {
    if (selected.size === 0) return;
    try {
      const { data } = await api.post("/admin/community/blooms/bulk", { ids: [...selected], released: true });
      toast.success(`Released ${data.updated} bloom${data.updated === 1 ? "" : "s"}`);
      load();
    } catch { toast.error("Could not bulk release"); }
  };

  return (
    <div data-testid="admin-community-page">
      <div className="pt-6">
        <Link to="/admin" className="chip" data-testid="admin-community-back">
          <ArrowLeft size={14} /> Admin
        </Link>
      </div>

      <PageHeader kicker="Admin · The Vault" title="Submissions" subtitle="Hidden until you release them" />

      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="text-ink-soft">
          <span data-testid="admin-pending-count">{pending.length}</span> pending ·{" "}
          <span data-testid="admin-released-count">{released.length}</span> released ·{" "}
          <span data-testid="admin-selected-count">{selected.size}</span> selected
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={load} className="chip" data-testid="admin-refresh">
            <RefreshCcw size={13} /> Refresh
          </button>
          <button onClick={selectAllPending} className="chip" data-testid="admin-select-all-pending">
            <CheckSquare size={13} /> Select pending
          </button>
          {selected.size > 0 && (
            <>
              <button onClick={clearSel} className="chip" data-testid="admin-clear-selection">
                <Square size={13} /> Clear
              </button>
              <button onClick={bulkRelease} className="btn-heritage text-sm py-2 px-3" data-testid="admin-bulk-release">
                <Send size={13} /> Release {selected.size}
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-center font-display italic text-ink-soft py-12">Opening the vault...</p>
      ) : blooms.length === 0 ? (
        <p className="text-center font-display italic text-ink-soft py-12" data-testid="admin-empty">No submissions yet.</p>
      ) : (
        <>
          <Section label="Pending" testid="admin-pending-section">
            {pending.length === 0
              ? <p className="font-display italic text-ink-soft text-center py-4">All caught up.</p>
              : pending.map((b) => <Row key={b.id} b={b} selectable selected={selected.has(b.id)} onToggleSel={() => toggleSel(b.id)} onToggle={setReleased} onRemove={remove} />)}
          </Section>
          <Section label="Released" testid="admin-released-section">
            {released.length === 0
              ? <p className="font-display italic text-ink-soft text-center py-4">Nothing released yet.</p>
              : released.map((b) => <Row key={b.id} b={b} onToggle={setReleased} onRemove={remove} />)}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ label, children, testid }) {
  return (
    <section className="mt-8" data-testid={testid}>
      <div className="kicker mb-3">{label}</div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Row({ b, selectable, selected, onToggleSel, onToggle, onRemove }) {
  return (
    <article className="h-card p-3 flex items-center gap-3" data-testid={`admin-row-${b.id}`}>
      {selectable && (
        <button
          onClick={onToggleSel}
          aria-label={selected ? "Deselect" : "Select"}
          className="shrink-0 w-8 h-8 flex items-center justify-center text-sage-dark"
          data-testid={`admin-row-select-${b.id}`}
        >
          {selected ? <CheckSquare size={20} /> : <Square size={20} />}
        </button>
      )}
      <img
        src={b.image_data_url}
        alt={b.variety_name}
        className="w-16 h-16 object-cover rounded-full bg-cream-deep shrink-0"
        draggable={false}
      />
      <div className="flex-1 min-w-0">
        <div className="font-display text-xl text-sage-dark truncate">{b.variety_name}</div>
        <div className="text-sm text-ink-soft truncate">
          {b.bloom_diameter_cm}cm · by {b.user_name}{b.user_garden ? `, ${b.user_garden}` : ""}
        </div>
        {b.note && <div className="font-display italic text-sm text-ink-soft truncate">"{b.note}"</div>}
        {b.released && b.released_at && (
          <div className="text-xs text-sage-dark mt-1" data-testid={`admin-row-log-${b.id}`}>
            Released by {b.released_by || "—"} on {new Date(b.released_at).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" })}
          </div>
        )}
      </div>
      <button
        onClick={() => onToggle(b.id, !b.released)}
        className="chip"
        data-active={b.released}
        data-testid={`admin-toggle-${b.id}`}
      >
        {b.released ? <Eye size={13} /> : <EyeOff size={13} />}
        {b.released ? "Released" : "Release"}
      </button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button className="chip" data-testid={`admin-delete-${b.id}`} aria-label="Delete">
            <Trash2 size={13} className="text-terracotta" />
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent className="bg-cream-soft border-heritage-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-sage-dark">Delete this submission?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-soft">
              Removes the bloom from the database entirely. Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-ghost">Keep</AlertDialogCancel>
            <AlertDialogAction
              className="btn-heritage"
              onClick={() => onRemove(b.id)}
              data-testid={`admin-delete-confirm-${b.id}`}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </article>
  );
}
